import { getFeatureFlags, getSecurityPinOpenV2 } from '@api/apiCall';
import { FEATURE_FLAGS } from '@root/constants';
import { getStore } from '@store/storeUtils';
import { CommonActions } from '@react-navigation/native';
import ScreenNames from '@navigation/navigationConstants';
import { isFeatureEnabled } from '@utils/utils';
import React, { useEffect, useRef } from 'react';
import { View, ActivityIndicator, StyleSheet } from 'react-native';

const GG_SEC_PIN_04 = 'GG-SEC-PIN-04';
const GG_SEC_PIN_09 = 'GG-SEC-PIN-09';

function navigateToPinVerifyLocked(navigation: any) {
  navigation.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [
        { name: ScreenNames.HOME },
        {
          name: ScreenNames.GG_VERIFY_PIN_WITH_EXPIRY,
          params: {
            type: 'verifyPinWithExpiry',
            source: 'pin_enforce_launch',
            lockedFromLaunch: true,
            onVerifyCallBack: () => {
              navigation.dispatch(
                CommonActions.reset({
                  index: 0,
                  routes: [{ name: ScreenNames.HOME }],
                }),
              );
            },
          },
        },
      ],
    }),
  );
}

/**
 * Gate screen shown on app launch when user has a session (employeeId).
 * When PIN_ENFORCE is true, calls GET security-pin v2 and redirects to
 * PIN Create (GG-SEC-PIN-04), PIN Verify, PIN Verify locked (GG-SEC-PIN-09), or Home.
 */
const PinEnforceGate: React.FC<{ navigation: any }> = ({ navigation }) => {
  const { employeeId, setFeatureFlags, setSecurityPinOpen, setSecurityPinLocked } = getStore();
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    (async () => {
      if (!employeeId || String(employeeId).length === 0) {
        navigation.dispatch(
          CommonActions.reset({
            index: 0,
            routes: [{ name: ScreenNames.LOGIN }],
          }),
        );
        return;
      }

      try {
        const flagsRes = await getFeatureFlags();
        const flags = flagsRes?.data ?? flagsRes ?? [];
        setFeatureFlags(flags);
        const pinEnforceResult = isFeatureEnabled(flags, FEATURE_FLAGS.PIN_ENFORCE);
        const pinEnforceEnabled = pinEnforceResult?.show === true;

        if (!pinEnforceEnabled) {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ name: ScreenNames.HOME }],
            }),
          );
          return;
        }

        try {
          const pinRes = await getSecurityPinOpenV2();
          const pinBody = pinRes?.data ?? pinRes;
          const errorCode = pinBody?.errorCode ?? pinBody?.error?.[0] ?? pinRes?.data?.error?.[0];
          if (errorCode === GG_SEC_PIN_04) {
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [
                  { name: ScreenNames.HOME },
                  {
                    name: ScreenNames.GG_CREATE_PIN,
                    params: { type: 'create', source: 'pin_enforce_launch' },
                  },
                ],
              }),
            );
            return;
          }
          if (errorCode === GG_SEC_PIN_09) {
            setSecurityPinLocked(true);
            navigateToPinVerifyLocked(navigation);
            return;
          }
          if (pinBody?.token != null || pinBody?.attempts != null || pinBody?.remainingAttempts != null) {
            setSecurityPinOpen({
              token: pinBody.token ?? null,
              attempts: pinBody.attempts,
              remainingAttempts: pinBody.remainingAttempts ?? 5,
            });
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [
                  { name: ScreenNames.HOME },
                  {
                    name: ScreenNames.GG_VERIFY_PIN_WITH_EXPIRY,
                    params: {
                      type: 'verifyPinWithExpiry',
                      source: 'pin_enforce_launch',
                      onVerifyCallBack: () => {
                        navigation.dispatch(
                          CommonActions.reset({
                            index: 0,
                            routes: [{ name: ScreenNames.HOME }],
                          }),
                        );
                      },
                    },
                  },
                ],
              }),
            );
            return;
          }
        } catch (_err: any) {
          const errResponse = _err?.response ?? _err;
          const errData = errResponse?.data ?? errResponse;
          const code = Array.isArray(_err)
            ? _err[0]
            : (errData?.errorCode ?? errData?.error?.[0]);
          if (code === GG_SEC_PIN_04) {
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [
                  { name: ScreenNames.HOME },
                  {
                    name: ScreenNames.GG_CREATE_PIN,
                    params: { type: 'create', source: 'pin_enforce_launch' },
                  },
                ],
              }),
            );
            return;
          }
          if (code === GG_SEC_PIN_09) {
            setSecurityPinLocked(true);
            navigateToPinVerifyLocked(navigation);
            return;
          }
        }
      } catch (_) {
        // Fall through to Home on any error
      }

      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: ScreenNames.HOME }],
        }),
      );
    })();
  }, [employeeId, navigation, setFeatureFlags, setSecurityPinOpen, setSecurityPinLocked]);

  return (
    <View style={styles.container}>
      <ActivityIndicator size="large" />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default PinEnforceGate;
